import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { getBusinessId, getSeoProfilePath } from '@/presentation/modules/profile/utils/reservationUrl';

export type MainNavigationItem = {
	routeAddress: string;
	routeName: string;
	label: string;
	iconName: string;
	activeForRule: string[];
	customRuleActivation?: boolean;
	hasBadge?: boolean;
	hiddenInMobileForRule?: string[];
};

type UserInformation = {
	role: string;
	userId?: string | number;
};

type BusinessInformation = Record<string, any>;

const BUSINESS_PROFILE_ROUTE_NAMES = [
	'ProfileShopId',
	'PublicProfileSlug',
	'ProfilePostId',
	'PublicProfileSlugPost',
	'ProfileEditPostId',
	'ProfileCreatePost',
	'ProfileSetting',
];

const DASHBOARD_SETTING_ROUTE_NAMES = [
	'DashboardSettingRoot',
	'DashboardSetting',
	'DashboardIdentityInformation',
	'DashboardArtists',
	'DashboardUpdateArtist',
	'DashboardCreateArtist',
	'DashboardServices',
	'DashboardServiceArtistAssignments',
	'DashboardMessageManager',
	'DashboarCcategory',
	'DashboardSendSms',
	'BusinessSetupSteps',
	'BusinessGuide',
];

const getBusinessProfileRouteAddress = (businessInfo: BusinessInformation, userInformation: UserInformation) => {
	const seoProfilePath = getSeoProfilePath(businessInfo, String(userInformation.userId || ''));
	if (seoProfilePath !== '/') return seoProfilePath;

	const businessId = getBusinessId(businessInfo, String(userInformation.userId || ''));
	return businessId ? `/profile/${businessId}` : '/';
};

export const useMainNavigationItems = (
	route: RouteLocationNormalizedLoaded,
	userInformation: ComputedRef<UserInformation>,
	businessInfo: ComputedRef<BusinessInformation> = computed(() => ({})),
) =>
	computed<MainNavigationItem[]>(() => [
		{
			routeAddress: '/dashboard/partner',
			routeName: 'partnerDashboard',
			label: 'همکاری من',
			iconName: 'medal-star',
			activeForRule: ['Partner'],
		},
		{
			routeAddress: '/dashboard/admin',
			routeName: 'DashboardAdmin',
			label: 'داشبورد',
			iconName: 'setting',
			activeForRule: ['Admin'],
		},
		{
			routeAddress: '/dashboard/admin/marketing-campaigns',
			routeName: 'adminMarketingCampaigns',
			label: 'کمپین‌ها',
			iconName: 'medal-star',
			activeForRule: ['Admin'],
		},
		{
			routeAddress: '/dashboard/admin/growth-center',
			routeName: 'adminGrowthCenter',
			label: 'مرکز رشد',
			iconName: 'dashboard',
			activeForRule: ['Admin'],
			customRuleActivation: String(route.name).startsWith('adminGrowthCenter'),
		},
		{
			routeAddress: '/admin/businesses',
			routeName: 'AdminBusinessesList',
			label: 'کسب‌وکارها',
			iconName: 'book',
			activeForRule: ['Admin'],
			customRuleActivation: ['AdminBusinessesList', 'AdminBusinessCreate', 'AdminBusinessEdit'].includes(String(route.name)),
		},
		{
			routeAddress: '/setting',
			routeName: 'Setting',
			label: 'تنظیمات',
			iconName: 'setting',
			activeForRule: ['User', 'AllUser'],
		},
		{
			routeAddress: '/dashboard',
			routeName: 'Index',
			label: 'خانه',
			iconName: 'dashboard',
			activeForRule: ['Business'],
		},
		{
			routeAddress: '/dashboard/requests',
			routeName: 'BookAppointment',
			label: 'نوبت‌ها',
			iconName: 'calendar',
			activeForRule: ['Business'],
			customRuleActivation: ['DashboardRequestRoot', 'DashboardRequests', 'DashboardReceiveRequests', 'DashboardSendRequests', 'BookAppointment'].includes(
				String(route.name),
			),
		},
		{
			routeAddress: '/dashboard/customer-club',
			routeName: 'DashboardCustomerClub',
			label: 'باشگاه',
			iconName: 'medal-star',
			activeForRule: ['Business'],
			hiddenInMobileForRule: ['Business'],
		},
		{
			routeAddress: '/dashboard/special-offers',
			routeName: 'DashboardSpecialOffers',
			label: 'پیشنهادهای ویژه',
			iconName: 'medal-star',
			activeForRule: ['Business'],
		},
		{
			routeAddress: getBusinessProfileRouteAddress(businessInfo.value, userInformation.value),
			routeName: 'BusinessProfile',
			label: 'پروفایل',
			iconName: 'user',
			activeForRule: ['Business'],
			customRuleActivation: BUSINESS_PROFILE_ROUTE_NAMES.includes(String(route.name)),
			hiddenInMobileForRule: ['Business'],
		},
		{
			routeAddress: '/dashboard/customers',
			routeName: 'DashboardCustomers',
			label: 'مشتریان',
			iconName: 'user',
			activeForRule: ['Business'],
		},
		{
			routeAddress: '/dashboard/wallet',
			routeName: 'DashboardWallet',
			label: 'کیف پول',
			iconName: 'money',
			activeForRule: ['Business'],
		},
		{
			routeAddress: '/requests',
			routeName: 'Request',
			label: 'رزروها',
			iconName: 'calendar',
			activeForRule: ['User', 'AllUser'],
		},
		{
			routeAddress: '/category',
			routeName: 'Category',
			label: 'دسته بندی ها',
			iconName: 'category',
			activeForRule: ['User', 'Admin', 'AllUser'],
		},
		{
			routeAddress: '/explore',
			routeName: 'Explore',
			label: 'اکسپلور',
			iconName: 'search',
			activeForRule: ['User', 'Admin', 'AllUser', 'Business'],
			hiddenInMobileForRule: ['Business'],
		},
		{
			routeAddress: '/home',
			routeName: 'Home',
			label: 'خانه',
			iconName: 'home',
			activeForRule: ['User', 'Admin', 'AllUser'],
			hiddenInMobileForRule: ['Business'],
		},
		{
			routeAddress: '/dashboard/setting',
			routeName: 'DashboardSetting',
			label: 'تنظیمات',
			iconName: 'setting',
			activeForRule: ['Business'],
			customRuleActivation: DASHBOARD_SETTING_ROUTE_NAMES.includes(String(route.name)),
		},
	]);
